import api from './api';

/**
 * Get all terms and conditions with pagination
 * @param {Object} params - Pagination parameters
 * @param {number} params.page - Page number
 * @param {number} params.pageSize - Number of items per page
 * @returns {Promise<Array>} - List of terms and conditions
 */
export const getTerms = async ({ page = 1, pageSize = 10 } = {}) => {
  try {
    const response = await api.get('/terms', {
      params: {
        page,
        page_size: pageSize,
      },
    });
    return {
      data: response.data.data,
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.page_size
    };
  } catch (error) {
    console.error('Error fetching terms and conditions:', error);
    throw error;
  }
};

/**
 * Get a specific terms and conditions by ID
 * @param {number} id - Terms and conditions ID
 * @returns {Promise<Object>} - Terms and conditions details
 */
export const getTermById = async (id) => {
  try {
    const response = await api.get(`/terms/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching terms and conditions with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Get the currently active terms and conditions
 * @returns {Promise<Object>} - Active terms and conditions
 */
export const getActiveTerms = async () => {
  try {
    const response = await api.get('/terms/active');
    return response.data;
  } catch (error) {
    console.error('Error fetching active terms and conditions:', error);
    throw error;
  }
};

/**
 * Create new terms and conditions
 * @param {Object} data - Terms and conditions data
 * @param {string} data.version - Version number
 * @param {string} data.content - Terms content
 * @param {boolean} data.is_active - Whether to activate these terms
 * @returns {Promise<Object>} - Created terms and conditions
 */
export const createTerms = async (data) => {
  try {
    const response = await api.post('/terms', data);
    return response.data;
  } catch (error) {
    console.error('Error creating terms and conditions:', error);
    throw error;
  }
};

/**
 * Update existing terms and conditions
 * @param {number} id - Terms and conditions ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} - Updated terms and conditions
 */
export const updateTerms = async (id, data) => {
  try {
    const response = await api.put(`/terms/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for cookies/sessions
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating terms and conditions with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Delete terms and conditions
 * @param {number} id - Terms and conditions ID
 * @returns {Promise<void>}
 */
export const deleteTerms = async (id) => {
  try {
    await api.delete(`/terms/${id}`);
  } catch (error) {
    console.error(`Error deleting terms and conditions with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Activate a specific terms and conditions version
 * @param {number} id - Terms and conditions ID to activate
 * @returns {Promise<Object>} - Updated terms and conditions
 */
export const activateTerms = async (id) => {
  try {
    const response = await api.put(`/terms/${id}/activate`);
    return response.data;
  } catch (error) {
    console.error(`Error activating terms and conditions with ID ${id}:`, error);
    throw error;
  }
};